import { $isListItemNode } from '@lexical/list';
import type { TextMatchTransformer } from '@lexical/markdown';
import { $isQuoteNode } from '@lexical/rich-text';
import { $findMatchingParent } from '@lexical/utils';
import {
  $createLineBreakNode,
  $createTextNode,
  $isElementNode,
  $isLineBreakNode,
  $isParagraphNode,
  $isTextNode,
  type LexicalNode,
  LineBreakNode,
} from 'lexical';
import { HTML_ENTITIES } from '../utils/html-entities';
import type { ConversionOnlyTransformer } from './conversion-only';
import { HTML_TEXT_FORMAT_EXPORTER } from './htmlTextFormats';

// Physical newlines cannot distinguish a trailing paragraph break from a
// leading one in the next paragraph. The attribute leaves legacy literal <br>
// text alone in internal imports and remains a break for HTML consumers.
const PARAGRAPH_BREAK = '<br data-lexical-line-break>';
const PARAGRAPH_BREAK_PATTERN =
  /<br data-lexical-line-break(?:="(backslash| {2,})")?>/;

// Replace the matched node without returning it for recursive Markdown parsing.
// Otherwise nested formatting can decode the same literal escape more than once.
export const LITERAL_CHARACTER_ESCAPE: ConversionOnlyTransformer<TextMatchTransformer> =
  {
    dependencies: [],
    importRegExp: /&#(?:36|38|60);/,
    regExp: /&#(?:36|38|60);/,
    replace: (node, match) => {
      node.replace(
        $createTextNode(String.fromCharCode(Number(match[0].slice(2, -1))))
          .setFormat(node.getFormat())
          .setStyle(node.getStyle())
      );
    },
    type: 'text-match',
    conversionOnly: true,
  };

const ENTITY_SUFFIX = new RegExp(
  `^(?:#(?:36|60|38);|${Object.keys(HTML_ENTITIES)
    .map((entity) => entity.slice(1))
    .join('|')})`,
  'i'
);
const ENTITY_LOOKAHEAD = Math.max(
  4,
  ...Object.keys(HTML_ENTITIES).map((entity) => entity.length - 1)
);

function followingText(node: LexicalNode): string {
  let cursor: LexicalNode = node;
  let text = '';
  while (text.length < ENTITY_LOOKAHEAD) {
    let next: LexicalNode | null = cursor.getNextSibling();
    while (!next) {
      const parent = cursor.getParent();
      if (!parent?.isInline()) return text;
      cursor = parent;
      next = cursor.getNextSibling();
    }
    while ($isElementNode(next) && next.isInline()) {
      const child: LexicalNode | null = next.getFirstChild();
      if (!child) return text;
      next = child;
    }
    if (!$isTextNode(next) || next.hasFormat('code')) return text;
    text += next.getTextContent().slice(0, ENTITY_LOOKAHEAD - text.length);
    cursor = next;
  }
  return text;
}

export const PARAGRAPH_LINE_BREAK: ConversionOnlyTransformer<TextMatchTransformer> =
  {
    dependencies: [LineBreakNode],
    export: (node, exportChildren, exportFormat) => {
      const block = $isLineBreakNode(node)
        ? $findMatchingParent(
            node,
            (parent) => $isElementNode(parent) && !parent.isInline()
          )
        : null;
      if (
        $isLineBreakNode(node) &&
        ($isParagraphNode(block) ||
          $isListItemNode(block) ||
          $isQuoteNode(block))
      ) {
        // Lexical persists CommonMark hard-break spelling in NodeState. Keep
        // it across internal saves so a later external export stays a hard break.
        const hardBreak = node.exportJSON().$?.mdHardLineBreak;
        return typeof hardBreak === 'string' && /^(\\| {2,})$/.test(hardBreak)
          ? `<br data-lexical-line-break="${hardBreak === '\\' ? 'backslash' : hardBreak}">`
          : PARAGRAPH_BREAK;
      }
      if (
        !$isTextNode(node) ||
        node.hasFormat('code') ||
        !/[<&$]/.test(node.getTextContent())
      ) {
        return null;
      }
      // A literal marker may span transparent comment/search marks. Escape
      // every literal opening bracket before adding formatting tags, rather
      // than requiring the complete marker to live in one TextNode.
      // Keep literal spellings of our escapes distinguishable from the
      // characters they encode, including after repeated save/load cycles.
      const suffix = followingText(node);
      const exportLiteral: typeof exportFormat = (textNode, content) =>
        exportFormat(
          textNode,
          content.replace(/&/g, (amp, offset: number) =>
            ENTITY_SUFFIX.test(content.slice(offset + 1) + suffix)
              ? '&#38;'
              : amp
          )
        )
          .replaceAll('<', '&#60;')
          .replaceAll('$', '&#36;');
      return (
        HTML_TEXT_FORMAT_EXPORTER.export?.(
          node,
          exportChildren,
          exportLiteral
        ) ?? exportLiteral(node, node.getTextContent())
      );
    },
    importRegExp: PARAGRAPH_BREAK_PATTERN,
    regExp: PARAGRAPH_BREAK_PATTERN,
    replace: (node, match) => {
      const lineBreak = $createLineBreakNode();
      if (match[1] !== undefined) {
        lineBreak.updateFromJSON({
          $: { mdHardLineBreak: match[1] === 'backslash' ? '\\' : match[1] },
        });
      }
      node.replace(lineBreak);
    },
    type: 'text-match',
    conversionOnly: true,
  };
